import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, Phone, Loader2 } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import NotFound from "@/pages/NotFound";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { resolveImg } from "@/hooks/usePublicContent";
import { CONTACT } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const [project, setProject] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    window.scrollTo(0, 0);
    let on = true;
    api.get(`/projects/${slug}`)
      .then((r) => on && setProject(r.data))
      .catch(() => on && setProject(null));
    return () => { on = false; };
  }, [slug]);

  if (project === undefined) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#9c7a4d]" />
      </div>
    );
  }
  if (project === null) return <NotFound />;

  const p = project;
  const title = `${p.title} | Perth Tiling WA`;
  const description = p.summary || `${p.service} tiling project in ${p.suburb}, Perth by Perth Tiling WA.`;
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Recent Projects", path: "/#projects" },
    { name: p.title, path: `/projects/${p.slug}` },
  ];
  const gallery = (p.gallery || []).filter(Boolean);

  return (
    <SmoothScroll>
      <Seo
        pageKey="home"
        overrideTitle={title}
        overrideDescription={description}
        service={p.service ? { name: p.service } : null}
        breadcrumb={breadcrumb}
      />
      <div className="grain-overlay" aria-hidden />
      <div data-testid={`project-page-${p.slug}`} className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        <section className="relative h-[68vh] min-h-[460px] w-full overflow-hidden">
          <img src={resolveImg(p.image)} alt={p.image_alt || `${p.title} — ${p.service} in ${p.suburb}, Perth`} fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/40 to-transparent" />
          <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-14">
            <nav className="mb-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <Link to="/#projects" className="hover:text-[#9c7a4d] transition-colors">Projects</Link>
              <span>/</span>
              <span className="text-[#0f172a]">{p.title}</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-4 flex items-center gap-2">
              {p.service}{p.suburb ? <><span className="text-[#9c7a4d]/50">·</span><MapPin className="h-3.5 w-3.5" /> {p.suburb}</> : null}
            </p>
            <h1 className="font-serif font-light text-[#0f172a] text-4xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
              <Reveal load>{p.title}</Reveal>
            </h1>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-[900px] px-6 md:px-12">
            <FadeUp>
              <p className="text-[#475569] text-lg md:text-xl font-light leading-relaxed whitespace-pre-line">{p.description || p.summary}</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-10 flex flex-wrap gap-4">
                <button onClick={openQuote} data-testid="project-quote-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  Get a Free Quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </FadeUp>
          </div>
        </section>

        {gallery.length > 0 && (
          <section className="pb-20 md:pb-28">
            <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {gallery.map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-8%" }} transition={{ duration: 0.7, delay: i * 0.08 }} className="relative overflow-hidden aspect-[4/5]">
                  <img src={resolveImg(src)} alt={`${p.title} detail ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        <section className="pb-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <Link to="/#projects" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
              <ArrowLeft className="h-4 w-4" /> All projects
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
